// Subscription plan catalogue. Each plan exposes the raw limits via the
// `limits` field so the backend can enforce quotas, while the derived fields
// (`charLimit`, `projectLimit`, …) keep backwards compatibility with the
// existing UI.

const UNLIMITED = Number.MAX_SAFE_INTEGER;
const GB = 1024 * 1024 * 1024;

const createPlan = ({ label, limits, support, aiTools, price, features, stripePriceId }) => {
    const normalizedLimits = {
        characters: limits.characters ?? UNLIMITED,
        projects: limits.projects ?? UNLIMITED,
        teamMembers: limits.teamMembers ?? UNLIMITED,
        targetLanguages: limits.targetLanguages ?? UNLIMITED,
        storageBytes: limits.storageBytes ?? UNLIMITED,
        apps: limits.apps ?? UNLIMITED,
        ocrCredits: limits.ocrCredits ?? 0,
    };

    return {
        label,
        limits: normalizedLimits,
        support,
        aiTools: aiTools ?? false,
        price: price ?? 'Contact Us',
        features: features ?? {},
        stripePriceId: stripePriceId ?? null,
        // Backwards compatible aliases used across the application today
        charLimit: normalizedLimits.characters,
        projectLimit: normalizedLimits.projects,
        teamLimit: normalizedLimits.teamMembers,
        maxTargetLanguages: normalizedLimits.targetLanguages,
        storageLimit: normalizedLimits.storageBytes,
    };
};

const ACCOUNT_PLANS = {
    free: createPlan({
        label: 'Free',
        limits: {
            characters: 0,
            projects: 5,
            teamMembers: 0,
            targetLanguages: 5,
            storageBytes: 500 * 1024 * 1024, // 500MB
            apps: 0,
            ocrCredits: 0,
        },
        support: 'email',
        aiTools: true,
        price: '£0',
        features: {
            fuzzyMatching: false,
            tmxExport: false,
            bulkGlossary: false,
            complexFormats: false,
            batchProcessing: false,
            externalSharing: false,
            teamAccess: false,
            chatAccess: false,
            analytics: false,
        },
    }),
    essential: createPlan({
        label: 'Essential',
        limits: {
            characters: 100_000,
            projects: 20,
            teamMembers: 0,
            targetLanguages: 25,
            storageBytes: 5 * GB,
            apps: 1,
            ocrCredits: 50,
        },
        support: 'email',
        aiTools: true,
        price: '£79',
        stripePriceId: process.env.STRIPE_PRICE_ID_ESSENTIAL || 'price_1T19DaH3EGdU5Kf3DDU5Xd8x',
        features: {
            fuzzyMatching: false,
            tmxExport: false,
            bulkGlossary: false,
            complexFormats: false,
            batchProcessing: false,
            externalSharing: false,
            teamAccess: false,
            chatAccess: false,
            analytics: false,
        },
    }),
    growth: createPlan({
        label: 'Growth',
        limits: {
            characters: 1_000_000,
            projects: 200,
            teamMembers: 10,
            targetLanguages: 100,
            storageBytes: 50 * GB,
            apps: 5,
            ocrCredits: 500,
        },
        support: 'premium',
        aiTools: true,
        price: '£249',
        stripePriceId: process.env.STRIPE_PRICE_ID_GROWTH,
        features: {
            fuzzyMatching: true,
            tmxExport: true,
            bulkGlossary: true,
            complexFormats: true,
            batchProcessing: true,
            externalSharing: true,
            teamAccess: true,
            chatAccess: true,
            analytics: true,
        },
    }),
    scale: createPlan({
        label: 'Scale',
        limits: {
            characters: UNLIMITED,
            projects: UNLIMITED,
            teamMembers: UNLIMITED,
            targetLanguages: UNLIMITED,
            storageBytes: UNLIMITED,
            apps: UNLIMITED,
            ocrCredits: 10_000,
        },
        support: 'dedicated',
        aiTools: true,
        price: 'Contact Us',
        features: {
            fuzzyMatching: true,
            tmxExport: true,
            bulkGlossary: true,
            complexFormats: true,
            batchProcessing: true,
            externalSharing: true,
            teamAccess: true,
            chatAccess: true,
            analytics: true,
            sso: true,
            apiAccess: true,
            auditLogs: true,
        },
    }),
};

module.exports = ACCOUNT_PLANS;
